// columns.ts
import { Select, DatePicker } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import utcPlugin  from "dayjs/plugin/utc";
import React, { useEffect, useState, useCallback } from "react";
import { request } from "../../../services";

import en from "antd/es/date-picker/locale/en_US";
import ua from "antd/es/date-picker/locale/uk_UA";

dayjs.extend(utcPlugin);

export const DATE_FMT = "DD.MM.YYYY";

export type Row = {
  key: React.Key;
  Controllers_id: number;
  Executor_type: number;
  Executors_FIO_id: number;
  Executors_FIO_name: string;
  Executors_id: number;
  Executors_name: string;
  dateK_plan: string;
  eta_actions_doc_id: number;   
  id: number;
  isMainExecutors: number;
  name_actions_doc: string;     
  num_pp: number;
  parent_id: number;
  user_id: number;
  contact_person: string;
  Rez_Execurors_FIO_id: number;
  ExecutorFIO: string;
  gta_executers_fio_id: number;
  bta_document_id: number;
  idNewRow?: null;
};

export type PatchRow = (id: number, patch: Partial<Row>) => void;
export type ActionOption = { value: number; label: string };

export function parseToDayjs(input?: string) {
  if (!input) return undefined;
  const custom = dayjs(input, [DATE_FMT, "DD-MM-YYYY"], true);
  if (custom.isValid()) return custom;
  const iso = dayjs(input);
  return iso.isValid() ? iso : undefined;
}



let cachedActions: ActionOption[] | null = null;
let fetching: Promise<ActionOption[]> | null = null;
// let actionMemState: ActionOption | null = null;

export function actionMemoryS(actionMemory: ActionOption | null) {
  // actionMemState = actionMemory;
}


async function fetchActionsOnce(): Promise<ActionOption[]> {
  if (cachedActions) return cachedActions;
  if (fetching) return fetching;

  fetching = (async () => {
    const res = (
      await request(`/documents/resolutions/getdatatableactions`, {
        method: "get",
        cancelTokenKey: "getdatatableactions",
      })
    )?.content;

    const opts: ActionOption[] = (res ?? []).map((it: any) => ({
      value: it.id,
      label: it.name,
    }));

    cachedActions = opts;
    return opts;
  })();

  try {
    return await fetching;
  } finally {
    fetching = null;
  }
}

/* ---------------- Компонент клітинки з Select (фетчить опції сам) ------------- */
function ActionCell({
  row,
  patchRow,
  intl
}: {
  row: Row;
  patchRow: PatchRow;
  intl:(key: string) => string
}) {
  const [options, setOptions] = useState<ActionOption[]>(cachedActions ?? []);
  const [loading, setLoading] = useState<boolean>(!cachedActions);
  // console.log("ROW CELL ", row);

  useEffect(() => {
    if (cachedActions) return; // вже є
    setLoading(true);
    fetchActionsOnce()
      .then(setOptions)
      .finally(() => setLoading(false));
  }, []);

  const ensureLoaded = useCallback(async () => {
    if (cachedActions) return; // вже завантажено
    setLoading(true);
    try {
      const opts = await fetchActionsOnce();
      setOptions(opts);
    } finally {
      setLoading(false);
    }
  }, []);

  return (
    <Select
      allowClear
      showSearch
      style={{ width: "100%" }}
      value={row.eta_actions_doc_id ?? undefined}
      options={options}
      loading={loading}
      placeholder={intl("choose-action")}
      optionFilterProp="label"
      onDropdownVisibleChange={(open) => open && ensureLoaded()}
      onChange={(val, option) => {
        const opt = option as unknown as ActionOption | undefined;
        patchRow(row.id, {
          eta_actions_doc_id: (val as number) ?? 0,
          name_actions_doc: opt?.label ?? "",
        });
      }}
    />
  );
}

/* ----------------------------- Фабрика колонок -------------------------------- */
export function makeColumns(patchRow: PatchRow, intl: (key: string)=> string): ColumnsType<Row> {
  
  return [
    {
      title: intl("executor"),
      dataIndex: "Executors_FIO_name",
      key: "Executors_FIO_name",
      width: 250,
      ellipsis: true,
    },
    {
      title: intl("date"),
      dataIndex: "dateK_plan",
      key: "dateK_plan",
      width: 150,
      ellipsis: true,
      render: (_: unknown, record: Row) => {
        const value = parseToDayjs(record.dateK_plan);
        return (
          <DatePicker
            locale={en}
            format={DATE_FMT}
            value={value}
            allowClear
            onChange={(d: Dayjs | null) => {
              const isoUtc = d ? d.startOf("day").utc().toISOString() : "";
              patchRow(record.id, { dateK_plan: isoUtc });
            }}
            style={{ width: "100%" }}
            placeholder={intl("choose-date-placeholder")}
          />
        );
      },
    },
    {
      title: intl("action"),
      dataIndex: "name_actions_doc",
      key: "name_actions_doc",
      width: 200,
      ellipsis: true,
      render: (_: unknown, record: Row) => {
        return <ActionCell row={record} patchRow={patchRow} intl={intl} />;
      },
    },
  ];
}

